import React from 'react';
import { Link } from '@hover/blueprint';
import { Flex, Box, Text, Button } from '@chakra-ui/react';
import FeatureList, { FeatureProps } from './FeatureList';

interface PlanHeaderProps {
  name: string;
  description: string;
  isRecommended?: boolean;
}

interface PlanBodyProps {
  price: number;
  jobDiscount: string;
  includesText?: string;
  features: FeatureProps[];
  isCustom?: boolean;
  isRecommended?: boolean;
  ctaButton: { label: string; href: string };
}

export type PlanColumnProps = PlanHeaderProps & PlanBodyProps;

const PlanHeader = ({ name, description, isRecommended }: PlanHeaderProps) => (
  <Box
    p={[7, 5, 5, 7]}
    position="relative"
    backgroundColor={isRecommended ? '#D3E6FA' : 'rgb(249, 252, 255)'}
    minHeight={{ md: '250px', xl: '220px' }}
  >
    {isRecommended && (
      <Box
        position="absolute"
        left="0"
        top="-56px"
        p="3"
        backgroundColor="#091F37"
        color="white"
        textAlign="center"
        fontWeight="bold"
        width="100%"
      >
        <Text fontSize="lg">Recommended</Text>
      </Box>
    )}
    <Text fontSize="2xl" fontWeight="bold" mb="3">
      {name}
    </Text>
    <Text display="block" fontSize="lg" pb="5" px={[5, 0, 0, 2]}>
      {description}
    </Text>
  </Box>
);

PlanHeader.defaultProps = {
  isRecommended: false,
};

const PlanBody = ({
  price,
  jobDiscount,
  includesText,
  features,
  isCustom,
  isRecommended,
  ctaButton,
}: PlanBodyProps) => (
  <Box p="8">
    {isCustom ? (
      <Text as="span" fontSize="4xl" fontWeight="bold">
        Custom
      </Text>
    ) : (
      <Box>
        <Text as="span" fontSize="4xl" fontWeight="bold">
          ${price}
        </Text>
        <Text as="span">/yr</Text>
      </Box>
    )}
    <Text color="hoverLightBlue" py="1">
      +
    </Text>
    <Text fontWeight="bold" fontSize="lg">
      ${jobDiscount} off per job
    </Text>
    <Button
      as={Link}
      href={ctaButton.href}
      size="lg"
      w="100%"
      variant={isRecommended ? undefined : 'outline'}
      my="8"
    >
      {ctaButton.label}
    </Button>

    <Box textAlign="start">
      {includesText && (
        <Text as="i" display="block" mb="5">
          {includesText}
        </Text>
      )}

      <FeatureList features={features} />
    </Box>
  </Box>
);

PlanBody.defaultProps = {
  includesText: undefined,
  isCustom: false,
  isRecommended: false,
};

const PlanColumn = ({
  name,
  description,
  price,
  jobDiscount,
  includesText,
  features,
  isCustom,
  isRecommended,
  ctaButton,
}: PlanColumnProps) => (
  <Flex w="100%" justifyContent="center">
    <Box
      borderWidth="thick"
      borderColor={isRecommended ? 'hoverLightBlue' : 'white'}
      boxShadow="0px 4px 24px rgb(0 0 0 / 25%)"
      textAlign="center"
      width="100%"
      maxW="md"
      height="100%"
    >
      <PlanHeader
        name={name}
        description={description}
        isRecommended={isRecommended}
      />
      <PlanBody
        ctaButton={ctaButton}
        price={price}
        jobDiscount={jobDiscount}
        includesText={includesText}
        features={features}
        isCustom={isCustom}
        isRecommended={isRecommended}
      />
    </Box>
  </Flex>
);

PlanColumn.defaultProps = {
  includesText: undefined,
  isCustom: false,
  isRecommended: false,
};

export default PlanColumn;
